import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SubPageHeader } from '@/components/ui/SubPageHeader';

export interface SettingsSubPageProps {
  title: string;
  children: ReactNode;
  onBack?: () => void;
  rightAction?: ReactNode;
  stickyFooter?: ReactNode;
}

export function SettingsSubPage({
  title,
  children,
  onBack,
  rightAction,
  stickyFooter,
}: SettingsSubPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  // If navigated from Activity (state.from), back goes there instead of browser history
  const handleBack =
    onBack ??
    (() => {
      const from = (location.state as { from?: string } | null)?.from;
      if (from) {
        navigate(from);
      } else {
        navigate(-1);
      }
    });

  return (
    // `flex-1 min-h-0` (not `h-dvh`): fills the available height inside the
    // AppShell's flex-column <main> rather than the full viewport. A hardcoded
    // h-dvh ran taller than its container whenever a global SystemBanner sat
    // above <main>, pushing the sticky footer (Connect/Reconnect CTA) below
    // the fold on mobile — the button only appeared after an unprompted scroll.
    <div className="flex flex-col flex-1 min-h-0 bg-surface">
      <SubPageHeader
        title={title}
        onBack={handleBack}
        rightAction={rightAction}
      />
      <div className="flex-1 flex flex-col overflow-y-auto min-h-0">
        {children}
      </div>
      {stickyFooter && (
        // No `border-t`: QA reported the hairline above the pinned CTA on
        // Household Profile ("Save changes") and Household Members > Add
        // member ("Send invite"), and Figma has no rule there on any
        // sub-page. `bg-background` is what separates the footer from the
        // content scrolling under it, exactly as in SubPageHeader.
        <div className="sticky bottom-0 bg-background px-5 py-4">
          {stickyFooter}
        </div>
      )}
    </div>
  );
}
